/*
 * Copyright (c) 2025. Sergey S. Chernov - All Rights Reserved
 *
 *  You may use, distribute and modify this code under the
 * terms of the private license, which you must obtain from the author
 *
 * To obtain the license, contact the author: https://t.me/real_sergeych or email to
 * real dot sergeych at gmail.
 */

package net.sergeych.crypto2

import com.ionspin.kotlin.crypto.secretbox.SecretBox
import com.ionspin.kotlin.crypto.secretbox.crypto_secretbox_KEYBYTES
import com.ionspin.kotlin.crypto.secretbox.crypto_secretbox_NONCEBYTES
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.Transient

/**
 * Symmetric key implements authenticated encrypting with random nonce and optional fill.
 * Random fill is normally used when cryptanalysis of the message size is a threat.
 *
 * Do not call this constructor directly, use [randomInt] or deserialize it.
 *
 * __Algorithms:__
 *
 * - Encryption: XSalsa20 stream cipher.
 * - Authentication: Poly1305 MAC
 */
@Serializable
@SerialName("sym")
class SymmetricKey(
    override val keyBytes: UByteArray,
    @Transient
    private val pbkdfParams: PBKD.Params?=null
) : EncryptingKey, DecryptingKey, UniversalKey() {

    override val magic: KeysmagicNumber = KeysmagicNumber.defaultSymmetric

    /**
     * @suppress
     * nonce + ciphered data serialization aid
     */
    @Serializable
    data class WithNonce(
        val cipherData: UByteArray,
        val nonce: UByteArray,
    ) {
        override fun equals(other: Any?): Boolean {
            if (this === other) return true
            if (other == null || this::class != other::class) return false

            other as WithNonce

            if (!cipherData.contentEquals(other.cipherData)) return false
            if (!nonce.contentEquals(other.nonce)) return false

            return true
        }

        override fun hashCode(): Int {
            var result = cipherData.contentHashCode()
            result = 31 * result + nonce.contentHashCode()
            return result
        }
    }

    override fun encryptWithNonce(plainData: UByteArray, nonce: UByteArray, randomFill: IntRange?): UByteArray {
        require(nonce.size == nonceLength)
        return SecretBox.easy(WithFill.encode(plainData, randomFill), nonce, keyBytes)
    }

    /**
     * Compact authenticated encryption with a caller-provided nonce.
     *
     * This method is meant for storage formats where the nonce is derived or stored elsewhere
     * and every byte of encoded output matters, for example short encrypted file names.
     * The output is the raw SecretBox ciphertext only: [plainData] size plus 16 authentication
     * bytes. The nonce is not stored in the output and must be reproduced exactly for
     * [decryptCompactWithNonce].
     *
     * Unlike [encryptWithNonce], this method does not wrap [plainData] with [WithFill]. For
     * short payloads this usually saves only 1 byte over [encryptWithNonce], because both
     * methods already require the caller to manage the nonce separately. Use it only when this
     * small size gain is important or when the surrounding format explicitly requires raw
     * SecretBox output.
     *
     * Never reuse the same nonce with the same key for different plaintext. Reusing a
     * `(key, nonce)` pair with SecretBox breaks the security of the stream cipher.
     */
    fun encryptCompactWithNonce(plainData: UByteArray, nonce: UByteArray): UByteArray {
        require(nonce.size == nonceLength)
        return SecretBox.easy(plainData, nonce, keyBytes)
    }

    override val nonceBytesLength: Int = nonceLength

    override val id by lazy {
        KeyId(KeysmagicNumber.defaultSymmetric,blake2b3l(keyBytes),pbkdfParams)
    }

    override fun decryptWithNonce(cipherData: UByteArray, nonce: UByteArray): UByteArray =
        protectDecryption {
            WithFill.decode(SecretBox.openEasy(cipherData, nonce, keyBytes))
        }

    /**
     * Compact authenticated decryption with a caller-provided nonce.
     *
     * Decrypts data produced by [encryptCompactWithNonce]. The nonce is not read from
     * [cipherData] and must be the exact same nonce that was used for encryption. Use this
     * method only for compact formats that intentionally omit both the nonce and the [WithFill]
     * wrapper.
     *
     * @throws DecryptionFailedException if the key, nonce, or cipher data is invalid.
     */
    fun decryptCompactWithNonce(cipherData: UByteArray, nonce: UByteArray): UByteArray =
        protectDecryption {
            require(nonce.size == nonceLength)
            SecretBox.openEasy(cipherData, nonce, keyBytes)
        }

    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other !is SymmetricKey) return false

        return keyBytes contentEquals other.keyBytes
    }

    override fun hashCode(): Int {
        return keyBytes.contentHashCode()
    }

    companion object {
        /**
         * Create a secure random symmetric key.
         */
        fun new() = SymmetricKey(SecretBox.keygen())

        val nonceLength = crypto_secretbox_NONCEBYTES
        val keyLength = crypto_secretbox_KEYBYTES
    }

}
